import { useMemo, useEffect, useState, useRef } from "react";
import axios from "axios";
import { CircularProgress, Pagination, Box } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import Readmore from "../Dashboard/Readmore";
import CustomdateD from "../Dashboard/CustomdateD";
import dayjs from "dayjs";
import { API_BASE_URL, LAMBDA_BASE_URL } from "../../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryParams } from "context/QueryParamsContext";
import { enUS } from "date-fns/locale";
import { useDomain } from "context/DomainContext";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import ExportC from "../Dashboard/ExportCustomers";

function DataTable({
  entriesPerPageOptions = [5, 10, 15, 20, 25],
  canSearch = false,
  date = false,
  csv = false,
  table,
  pagination = { variant: "gradient", color: "info" },
  isSorted = true,
  noEndBorder = false,
  dashboardDateRange,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const [rows, setRows] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "registered_on",
    direction: "desc",
  });

  const location = useLocation();
  const navigate = useNavigate();

  const { dateRange, setDateRange } = useQueryParams();
  const { domainId } = useDomain();

  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (!domainId) {
      navigate("/Access-Denied");
    }
  }, [domainId, navigate]);

  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const start = (page - 1) * entriesPerPage + 1;
  const end = Math.min(page * entriesPerPage, totalEntries);

  useEffect(() => {
    if (dashboardDateRange?.start_date && dashboardDateRange?.end_date) {
      setDateRange(dashboardDateRange);
    }
  }, [dashboardDateRange, setDateRange]);

  const fetchUsers = async () => {
    if (!domainId) return; 

    setLoading(true);
    try {
      const { start_date, end_date } = dateRange;
      const offset = (page - 1) * entriesPerPage;

      const res = await axios.get(`${LAMBDA_BASE_URL}/all-domains`, {
        params: {
          limit: entriesPerPage,
          offset,
          search,
          sort_key: sortConfig.key,
          sort_direction: sortConfig.direction,
          start_date,
          end_date,
        },
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      });

      const formattedRows = res.data.result.domains.map((domain) => ({
        id: domain.id,
        website: domain.website,
        total_queries: domain.total_queries,
        source: domain.source,
        registered_on: domain.registered_on,
        domainId: domain.domainId,
      }));

      setRows(formattedRows);
      setTotalEntries(res.data.result.total);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      fetchUsers();
    } else {
      if (domainId) {
        fetchUsers();
      }
    }
  }, [page, entriesPerPage, search, sortConfig, dateRange, domainId]);

  const sortedRows = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (sortConfig.key === "registered_on") {
        return sortConfig.direction === "asc"
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }

      if (sortConfig.key === "total_queries") {
        return sortConfig.direction === "asc"
          ? Number(aVal) - Number(bVal)
          : Number(bVal) - Number(aVal);
      }

      if (aVal === bVal) return 0;
      return sortConfig.direction === "asc"
        ? aVal > bVal
          ? 1
          : -1
        : aVal < bVal
        ? 1
        : -1;
    });
  }, [rows, sortConfig]);

  const handlePageChange = (event, value) => setPage(value);

  const handleEntriesPerPageChange = ({ value }) => {
    setEntriesPerPage(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey
          ? prev.direction === "desc"
            ? "asc"
            : "desc"
          : "asc",
    }));
  };

  const handleDateRangeSelect = ({ start, end }) => {
    setDateRange({ start_date: start, end_date: end });
    sessionStorage.setItem("saved_start_date", start);
    sessionStorage.setItem("saved_end_date", end);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("start_date", start);
    queryParams.set("end_date", end);
    navigate({ pathname: location.pathname, search: queryParams.toString() });
  };

  return (
    <TableContainer sx={{ boxShadow: "none", minHeight: "450px" }}>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <h4 style={{ textAlign: "left", padding: "10px" }}>Query Details</h4>

          {(entriesPerPageOptions || canSearch || date || csv) && (
            <SoftBox
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              gap={2}
              p={2}
            >
              <SoftBox
                display="flex"
                alignItems="center"
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                <SoftSelect
                  defaultValue={{
                    value: entriesPerPage,
                    label: entriesPerPage,
                  }}
                  options={entriesPerPageOptions.map((entry) => ({
                    value: entry,
                    label: entry,
                  }))}
                  onChange={handleEntriesPerPageChange}
                  size="small"
                />
                <SoftTypography variant="caption" color="secondary" ml={1}>
                  entries per page
                </SoftTypography>
              </SoftBox>

              <SoftBox
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                alignItems={{ xs: "stretch", md: "center" }}
                gap={2}
                width={{ xs: "100%", md: "auto" }}
              >
                {canSearch && (
                  <SoftBox sx={{ width: { xs: "100%", md: "12rem" } }}>
                    <SoftInput
                      placeholder="Search..."
                      value={search}
                      onChange={handleSearchChange}
                      fullWidth
                    />
                  </SoftBox>
                )}

                {date && (
                  <SoftBox
                    sx={{
                      width: { xs: "100%", md: "auto" },
                      ml: { xs: 0, md: 0 },
                    }}
                  >
                    <CustomdateD onRangeSelect={handleDateRangeSelect} />
                  </SoftBox>
                )}

                {csv && (
                  <SoftBox
                    sx={{
                      width: { xs: "100%", md: "auto" },
                      ml: { xs: 0, md: 0 },
                    }}
                  >
                    <ExportC
                      searchQuery={search}
                      startDate={dateRange?.start_date}
                      endDate={dateRange?.end_date}
                      filename="ansera_customers_details.csv"
                      columnDefs={columns}
                    />
                  </SoftBox>
                )}
              </SoftBox>
            </SoftBox>
          )}

          <Table>
            <SoftBox component="thead">
              {columns?.map((column, key) => {
                const isSortable = !["Content"].includes(column.Header);

                return (
                  <DataTableHeadCell
                    key={key}
                    width={column.width || "auto"}
                    align={column.align || "left"}
                    onClick={
                      isSortable ? () => handleSort(column.accessor) : undefined
                    }
                    sorted={
                      isSortable && sortConfig.key === column.accessor
                        ? sortConfig.direction
                        : "none"
                    }
                    style={{
                      cursor: isSortable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    {column.Header}
                  </DataTableHeadCell>
                );
              })}
            </SoftBox>

            <TableBody>
              {sortedRows.map((row, key) => (
                <TableRow key={key} sx={{ padding: 4 }}>
                  {columns.map((col, index) => (
                    <DataTableBodyCell
                      key={index}
                      align={col.align || "left"}
                    >
                      {col.accessor === "website" ? (
                        <span
                          style={{
                            color: "teal",
                            cursor: "pointer",
                            fontWeight: 500,
                          }}
                          onClick={() => {
                            sessionStorage.setItem("domainId", row.domainId);
                            sessionStorage.setItem("domain_name",row.website);
                            sessionStorage.setItem("role",null);
                            sessionStorage.setItem("source",row.source);
                            navigate("/dashboard");
                          }}
                        >
                          {row[col.accessor]}
                        </span>
                      ) : col.accessor === "total_queries" ? (
                        <b>{row[col.accessor]}</b>
                      ) : col.accessor === "registered_on" ? (
                        dayjs(row[col.accessor]).format("MMM D, YYYY hh:mm A")
                      ) : (
                        row[col.accessor]
                      )}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>

            <SoftBox
              pb={1}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              paddingTop="10px"
              paddingLeft="40px"
            >
              <SoftTypography variant="button" color="secondary">
                Showing {new Intl.NumberFormat(enUS).format(start)} to{" "}
                {new Intl.NumberFormat(enUS).format(end)} of{" "}
                {new Intl.NumberFormat(enUS).format(totalEntries)} entries
              </SoftTypography>
            </SoftBox>
          </Table>

          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              shape="circular"
              color="info"
              sx={{
                "& .MuiPaginationItem-root": {
                  "&.Mui-selected": {
                    background: "#219297",
                    color: "white !important",
                    "&:hover": {
                      background: "teal",
                    },
                  },
                  "&:not(.Mui-selected)": {
                    color: "black",
                  },
                },
              }}
            />
            <SoftTypography variant="button">
              Page {new Intl.NumberFormat(enUS).format(page)} of{" "}
              {new Intl.NumberFormat(enUS).format(totalPages)}
            </SoftTypography>
          </SoftBox>
        </>
      )}
    </TableContainer>
  );
}

export default DataTable;
